import { Tooltip } from 'antd'
import { diff } from 'deep-object-diff'
import { lowerCase } from 'lodash'
import moment from 'moment'

import { isPrimitive } from './auditLogHelpers'

import { Text } from '@/components/shared-components/BUI/Text'
import { LocaleContextValue } from '@/context/localeContext'
import { useTimezonedDates } from '@/context/timezoneContext'
import { convertEnumToString, createCommaSeparatedString } from '@/utils/index'

import { AuditAction, AuditResource } from 'types/api.graphql'
import { AuditLogLike } from 'types/vagueTypes'

export type AuditLogVerb =
  | 'Set'
  | 'Removed'
  | 'Changed'
  | 'Created'
  | 'Sent'
  | 'Archived'
  | 'Restored'
  | 'Generated'

type Diff = {
  key: string
  verb: AuditLogVerb
  conjunction?: string
  oldValue: React.ReactNode | null
  newValue: React.ReactNode | null
}

export const getChangedKeys = (oldSnap, newSnap, includeIdKeys: boolean) => {
  if (!oldSnap || !newSnap) return []

  const difference = { ...diff(oldSnap, newSnap) }

  delete difference['updatedAt']

  const keys = Object.keys(difference).filter((key) => {
    const newValue = newSnap[key]
    const oldValue = oldSnap[key]

    // Only consider keys where both new and old value are primitive types, otherwise exclude from list of changed
    if (!isPrimitive(newValue) && !isPrimitive(oldValue)) {
      return false
    }

    const newValueAsDate = moment(newValue)
    const oldValueAsDate = moment(oldValue)

    if (newValueAsDate.isValid() && oldValueAsDate.isValid()) {
      if (newValueAsDate.isSame(oldValueAsDate, 'second')) return false // exclude dates where milliseconds are different
    }

    return true
  })

  return includeIdKeys ? keys : keys.filter((key) => !key.endsWith('Id'))
}

const getVerb = (oldSnapItem, newSnapItem): Diff['verb'] => {
  if (
    (oldSnapItem === null || oldSnapItem === '') &&
    (newSnapItem !== null || newSnapItem !== '')
  ) {
    return 'Set'
  }
  if (
    (newSnapItem === null || newSnapItem === '') &&
    (oldSnapItem !== null || oldSnapItem !== '')
  ) {
    return 'Removed'
  }

  return 'Changed'
}

const formatValue = (
  value: string,
  formatDateString: ReturnType<typeof useTimezonedDates>['formatDateString']
) => {
  if (moment(value).isValid()) {
    return formatDateString({ date: value })
  }

  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE'
  }

  return convertEnumToString(value)
}

type RecipientContext = {
  type: 'contact' | 'worker' | 'adminUser'
  id: string
  companyName: string
  contactName: string
  contactEmail: string
}

export const AUDIT_RESOURCE_TITLES: Partial<Record<AuditResource, string>> = {
  [AuditResource.Induction]: 'induction:title',
  [AuditResource.InductionInvite]: 'induction:labels.inductionInvite',
  [AuditResource.InductionTemplate]: 'induction:labels.inductionTemplate',
  [AuditResource.LibrarySwmsDoc]: 'librarySwms:labels.librarySwmsDoc',
  [AuditResource.Subcontractor]: 'subcontractor:title',
  [AuditResource.SwmsDoc]: 'swms:labels.swmsDoc',
  [AuditResource.SwmsDocLog]: 'swms:labels.swmsDocLog',
  [AuditResource.Ticket]: 'ticket:title',
  [AuditResource.TicketReview]: 'ticket:labels.ticketReview',
}

export const getAuditLogDiff =
  (
    formatDateString: ReturnType<typeof useTimezonedDates>['formatDateString']
  ) =>
  (
    auditLog: AuditLogLike,
    translate: LocaleContextValue['t'],
    includeIdKeys = true
  ): Diff[] => {
    const title = translate(
      AUDIT_RESOURCE_TITLES[auditLog.resource] ??
        convertEnumToString(auditLog.resource)
    )

    if (auditLog.action === AuditAction.Create) {
      return [
        {
          key: title,
          verb: 'Created',
          conjunction: 'this',
          oldValue: null,
          newValue: null,
        },
      ]
    }

    if (auditLog.action === AuditAction.Archive) {
      return [
        {
          key: title,
          verb: 'Archived',
          conjunction: 'this',
          oldValue: null,
          newValue: null,
        },
      ]
    }

    if (auditLog.action === AuditAction.Restore) {
      return [
        {
          key: title,
          verb: 'Restored',
          conjunction: 'this',
          oldValue: null,
          newValue: null,
        },
      ]
    }

    if (auditLog.action === AuditAction.Share) {
      const recipient = auditLog.newSnapshot as RecipientContext

      return [
        {
          key: title,
          verb: 'Sent',
          oldValue: null,
          newValue: recipient ? (
            <>
              <Tooltip title={recipient.contactEmail}>
                <Text
                  variant="caption01"
                  stronger
                  secondary="base"
                  style={{ cursor: 'pointer' }}
                >
                  {recipient.contactName}
                </Text>
              </Tooltip>{' '}
              from{' '}
              <Text variant="caption01" stronger secondary="base">
                {recipient.companyName}
              </Text>
            </>
          ) : null,
        },
      ]
    }

    if (auditLog.action === AuditAction.Message) {
      const contacts = auditLog.newSnapshot?.metadata?.contactNames ?? []

      const names = contacts ? createCommaSeparatedString(contacts) : null

      return [
        {
          key: title,
          verb: 'Sent',
          conjunction: 'this',
          oldValue: null,
          newValue: names,
        },
      ]
    }

    if (auditLog.action === AuditAction.GenerateSummary) {
      return [
        {
          key: title,
          verb: 'Generated',
          conjunction: 'a summary on this',
          oldValue: null,
          newValue: null,
        },
      ]
    }

    const keys = getChangedKeys(
      auditLog.oldSnapshot,
      auditLog.newSnapshot,
      includeIdKeys
    )

    const diffs: Diff[] = []

    if (keys.length === 0) {
      diffs.push({
        key: title,
        verb: 'Changed',
        conjunction: 'this',
        oldValue: null,
        newValue: null,
      })
    }

    keys.forEach((key) => {
      const newValue = auditLog.newSnapshot[key]
      const oldValue = auditLog.oldSnapshot[key]

      const verb = getVerb(auditLog.oldSnapshot[key], auditLog.newSnapshot[key])

      diffs.push({
        key: lowerCase(key),
        verb,
        oldValue:
          verb == 'Removed' ? null : formatValue(oldValue, formatDateString),
        newValue:
          verb == 'Removed' ? null : formatValue(newValue, formatDateString),
      })
    })

    return diffs
  }

export const useAuditLogDiff = () => {
  const { formatDateString } = useTimezonedDates()
  return {
    getAuditLogDiff: getAuditLogDiff(formatDateString),
  }
}
